import type { IndicatorRegistryEntry } from "lightweight-charts-indicators"
import type { IndicatorRenderer, PlotOverride, RenderableIndicator } from "lightweight-charts-indicators/render"
import { isTransparent, normalizeColor, opaque, sameColor } from "../../ui/color.ts"
import type {
  FillStyle,
  IndicatorStyle,
  LevelStyle,
  LineStyleName,
  LineWidth,
  PlotStyle,
  PlotType
} from "../workspace/indicatorStyleSchema.ts"
import type { IndicatorInstance } from "../workspace/workspaceModel.ts"

// An indicator instance's style (TradingView's Style tab) applied to what the
// renderer draws, without touching the vendored registry or a computed result:
// both are shallow-copied. Pure; IndicatorLayer and the settings dialog use it.

export type IndicatorResult = Parameters<IndicatorRenderer["render"]>[1]
type PlotConfig = RenderableIndicator["plotConfig"][number]
type HLineConfig = NonNullable<RenderableIndicator["hlineConfig"]>[number]
type FillConfig = NonNullable<RenderableIndicator["fillConfig"]>[number]
type Point = { readonly time: number; readonly value: number; readonly color?: string }

const DEFAULT_PLOT_COLOR = "#2962FF"

/** An entry with its instance's style applied, computed once per indicator change (not per tick). */
export interface Styled {
  readonly entry: IndicatorRegistryEntry
  readonly style: IndicatorStyle
  readonly renderable: RenderableIndicator
  /** Overrides by plot id; per-bar palette replacements are added per result (`renderOptions`). */
  readonly plots: Record<string, PlotOverride>
  readonly lastValueVisible: boolean
  readonly precision: number
  /** Number format of the indicator's values (volume abbreviates; percent adds %). */
  readonly format: "price" | "volume" | "percent"
}

export const plotStyleOf = (style: IndicatorStyle | undefined, id: string): PlotStyle => style?.plots?.[id] ?? {}
const levelStyleOf = (style: IndicatorStyle | undefined, id: string): LevelStyle => style?.levels?.[id] ?? {}
const fillStyleOf = (style: IndicatorStyle | undefined, id: string): FillStyle => style?.fills?.[id] ?? {}

/** The precision TradingView would show: the user's, the indicator's own format, else the symbol's. */
const precisionOf = (entry: IndicatorRegistryEntry, style: IndicatorStyle, symbolPrecision: number): number =>
  style.precision ??
    entry.format?.precision ??
    (entry.format?.type === "volume" ? 0 : entry.format !== undefined ? 2 : symbolPrecision)

export const styleEntry = (
  entry: IndicatorRegistryEntry,
  style: IndicatorStyle | undefined,
  symbolPrecision: number
): Styled => {
  const s = style ?? {}
  const plotConfig = entry.plotConfig.map((plot): PlotConfig => {
    const lineStyle = plotStyleOf(s, plot.id).lineStyle
    return lineStyle === undefined ? plot : { ...plot, linestyle: lineStyle }
  })
  const hlineConfig = entry.hlineConfig?.map((level): HLineConfig => {
    const own = levelStyleOf(s, level.id)
    return {
      ...level,
      ...(own.color !== undefined ? { color: own.color } : {}),
      ...(own.lineWidth !== undefined ? { linewidth: own.lineWidth } : {}),
      ...(own.lineStyle !== undefined ? { linestyle: own.lineStyle } : {}),
      ...(own.price !== undefined ? { price: own.price } : {}),
      ...(own.visible === false ? { display: "none" as const } : {})
    }
  })
  // The renderer ignores FillConfig.visible: a hidden fill is left out.
  const fillConfig = entry.fillConfig
    ?.filter((fill) => fillStyleOf(s, fill.id).visible !== false)
    .map((fill): FillConfig => {
      const color = fillStyleOf(s, fill.id).color
      return color === undefined ? fill : { ...fill, color }
    })
  const plots: Record<string, PlotOverride> = {}
  for (const plot of entry.plotConfig) {
    const own = plotStyleOf(s, plot.id)
    const override: PlotOverride = {
      // Only ever "false": an override wins over display: "none" and input-driven visibility.
      ...(own.visible === false ? { visible: false } : {}),
      ...(own.color !== undefined ? { color: own.color } : {}),
      ...(own.lineWidth !== undefined ? { lineWidth: own.lineWidth } : {}),
      ...(own.type !== undefined ? { style: own.type } : {})
    }
    if (Object.keys(override).length > 0) plots[plot.id] = override
  }
  return {
    entry,
    style: s,
    renderable: {
      overlay: entry.overlay,
      plotConfig,
      ...(hlineConfig ? { hlineConfig } : {}),
      ...(fillConfig ? { fillConfig } : {}),
      ...(entry.arrowConfig ? { arrowConfig: entry.arrowConfig } : {})
    },
    plots,
    lastValueVisible: s.labelsOnPriceScale ?? true,
    precision: precisionOf(entry, s, symbolPrecision),
    format: entry.format?.type ?? "price"
  }
}

const pointsOf = (result: IndicatorResult, plotId: string): ReadonlyArray<Point> =>
  (result.plots[plotId] ?? []) as ReadonlyArray<Point>

/**
 * Per-bar colours of a plot mapped to the user's choice. A plot with a palette
 * (MACD's histogram): each colour drawn is matched to its palette entry and
 * replaced by that entry's override. A plot without one whose points still carry
 * colours (the renderer draws those over the plot colour): every colour drawn
 * becomes the chosen colour.
 */
const paletteReplacements = (plot: PlotConfig, own: PlotStyle, points: ReadonlyArray<Point>): Record<string, string> | null => {
  const palette = plot.palette
  const byIndex = palette !== undefined && own.palette !== undefined && Object.keys(own.palette).length > 0
  const single = palette === undefined && own.color !== undefined
  if (!byIndex && !single) return null
  const replacements: Record<string, string> = {}
  const resolved = new Set<string>()
  for (const point of points) {
    const drawn = point.color
    if (drawn === undefined || resolved.has(drawn)) continue
    resolved.add(drawn)
    if (single) {
      if (!isTransparent(drawn)) replacements[drawn] = own.color!
      continue
    }
    const index = palette!.findIndex((entry) => sameColor(entry, drawn))
    const replacement = index < 0 ? undefined : own.palette![String(index)]
    if (replacement !== undefined) replacements[drawn] = replacement
  }
  return Object.keys(replacements).length === 0 ? null : replacements
}

/** What to pass the renderer for one result: the result with fills styled, and the plot overrides. */
export const renderOptions = (styled: Styled, result: IndicatorResult): {
  readonly result: IndicatorResult
  readonly plots: Record<string, PlotOverride>
} => {
  const plots: Record<string, PlotOverride> = { ...styled.plots }
  for (const plot of styled.entry.plotConfig) {
    const replacements = paletteReplacements(plot, plotStyleOf(styled.style, plot.id), pointsOf(result, plot.id))
    if (replacements !== null) plots[plot.id] = { ...plots[plot.id], palette: replacements }
  }
  const fills = result.fills
  if (fills === undefined || styled.style.fills === undefined) return { result, plots }
  const styledFills = fills.flatMap((fill) => {
    const own = fill.id === undefined ? {} : fillStyleOf(styled.style, fill.id)
    if (own.visible === false) return []
    if (own.color === undefined || fill.gradient !== undefined) return [fill]
    // transp would scale the chosen opacity again; per-bar colours give way to the chosen one.
    const { transp: _transp, ...options } = fill.options ?? {}
    return [{ ...fill, options: { ...options, color: own.color }, ...(fill.colors ? { colors: fill.colors.map(() => own.color!) } : {}) }]
  })
  return { result: { ...result, fills: styledFills }, plots }
}

// --- Legend --------------------------------------------------------------------

export interface LegendValue {
  readonly title: string
  readonly color: string
  readonly value: number
  /** As TradingView shows it (the indicator's precision and format). */
  readonly text: string
}

/** The point at `time` (epoch seconds), or the newest finite one when `time` is null. */
const pointAt = (points: ReadonlyArray<Point>, time: number | null): Point | null => {
  if (time === null) {
    for (let i = points.length - 1; i >= 0 && i >= points.length - 50; i--) {
      if (Number.isFinite(points[i]!.value)) return points[i]!
    }
    return null
  }
  let lo = 0
  let hi = points.length - 1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    const at = points[mid]!.time
    if (at === time) return Number.isFinite(points[mid]!.value) ? points[mid]! : null
    if (at < time) lo = mid + 1
    else hi = mid - 1
  }
  return null
}

/** Whether the renderer draws a plot (same rules), and whether it belongs in the legend. */
const inLegend = (plot: PlotConfig, own: PlotStyle, inputs: Record<string, unknown>, result: IndicatorResult): boolean => {
  if (own.visible === false) return false
  if (plot.display !== undefined && plot.display !== "all" && plot.display !== "status_line") return false
  if (plot.visible === false) return false
  if (typeof plot.visible === "string") {
    const visibility = (result as { visibility?: Record<string, unknown> }).visibility
    if (!(inputs[plot.visible] ?? visibility?.[plot.visible] ?? true)) return false
  }
  return true
}

/**
 * The values TradingView's status line shows for an indicator at a time (or its
 * latest bar), in the colours drawn: by plot id, so a hidden or skipped plot
 * never shifts the others.
 */
export const legendValues = (
  styled: Styled,
  result: IndicatorResult,
  inputs: Record<string, unknown>,
  time: number | null
): ReadonlyArray<LegendValue> => {
  if (styled.style.valuesInStatusLine === false) return []
  const values: Array<LegendValue> = []
  for (const plot of styled.entry.plotConfig) {
    const own = plotStyleOf(styled.style, plot.id)
    if (!inLegend(plot, own, inputs, result)) continue
    const point = pointAt(pointsOf(result, plot.id), time)
    if (point === null) continue
    const drawn = point.color !== undefined && !isTransparent(point.color) ? point.color : undefined
    const replaced = drawn === undefined ? null : paletteReplacements(plot, own, [point])?.[drawn] ?? null
    values.push({
      title: plot.title ?? plot.id,
      color: replaced ?? drawn ?? own.color ?? plot.color ?? DEFAULT_PLOT_COLOR,
      value: point.value,
      text: formatLegendValue(point.value, styled)
    })
  }
  return values
}

/** A legend number as TradingView shows it: fixed decimals, abbreviated volume, or a percentage. */
export const formatLegendValue = (value: number, styled: Pick<Styled, "precision" | "format">): string => {
  if (styled.format === "volume") {
    return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 }).format(value)
  }
  const text = value.toFixed(styled.precision)
  return styled.format === "percent" ? `${text}%` : text
}

// --- Style tab -----------------------------------------------------------------

export interface PlotRow {
  readonly id: string
  readonly title: string
  readonly color: string
  readonly lineWidth: LineWidth
  readonly lineStyle: LineStyleName
  readonly type: PlotType
  /** Per-bar colours the plot picks from (one swatch each), when it has a fixed set. */
  readonly palette: ReadonlyArray<string> | null
}

export interface LevelRow {
  readonly id: string
  readonly title: string
  readonly color: string
  readonly lineWidth: LineWidth
  readonly lineStyle: LineStyleName
  readonly price: number
}

export interface FillRow {
  readonly id: string
  readonly title: string
  readonly color: string | null
}

const asWidth = (width: number | undefined): LineWidth => (width === 2 || width === 3 || width === 4 ? width : 1)

/** The rows of the Style tab with the indicator's defaults (what "Reset" goes back to). */
export const styleRows = (entry: IndicatorRegistryEntry): {
  readonly plots: ReadonlyArray<PlotRow>
  readonly levels: ReadonlyArray<LevelRow>
  readonly fills: ReadonlyArray<FillRow>
} => ({
  plots: entry.plotConfig
    .filter((plot) => plot.display !== "none" && plot.editable !== false)
    .map((plot) => ({
      id: plot.id,
      title: plot.title ?? plot.id,
      color: normalizeColor(plot.color ?? DEFAULT_PLOT_COLOR) ?? DEFAULT_PLOT_COLOR,
      lineWidth: asWidth(plot.lineWidth),
      lineStyle: plot.linestyle ?? "solid",
      type: plot.style ?? "line",
      palette: plot.palette?.map((color) => normalizeColor(color) ?? DEFAULT_PLOT_COLOR) ?? null
    })),
  levels: (entry.hlineConfig ?? [])
    .filter((level) => level.editable !== false && level.display !== "none")
    .map((level) => ({
      id: level.id,
      title: level.title ?? String(level.price),
      color: normalizeColor(level.color ?? "#787B86") ?? "#787B86",
      lineWidth: asWidth(level.linewidth),
      lineStyle: level.linestyle ?? "dashed",
      price: level.price
    })),
  fills: (entry.fillConfig ?? [])
    .filter((fill) => fill.editable !== false)
    .map((fill) => ({
      id: fill.id,
      title: fill.title ?? "Background",
      // Gradients and per-bar colours: only shown or hidden.
      color: fill.gradient === undefined && fill.colors === undefined && fill.color !== undefined
        ? normalizeColor(fill.color) ?? null
        : null
    }))
})

const prune = <A extends Record<string, unknown>>(value: A): A | undefined => {
  const kept = Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as A
  return Object.keys(kept).length === 0 ? undefined : kept
}

const pruneRecord = <A>(record: Record<string, A | undefined>): Record<string, A> | undefined => {
  const kept = Object.fromEntries(Object.entries(record).filter(([, v]) => v !== undefined)) as Record<string, A>
  return Object.keys(kept).length === 0 ? undefined : kept
}

/**
 * Only what differs from the indicator's defaults (colours compared by value, so
 * "rgba(33, 150, 243, 0.05)" equals "#2196F30D"); undefined when nothing does.
 */
export const pruneStyle = (entry: IndicatorRegistryEntry, style: IndicatorStyle): IndicatorStyle | undefined => {
  const rows = styleRows(entry)
  const differs = <V>(value: V | undefined, base: V, same: (a: V, b: V) => boolean = (a, b) => a === b) =>
    value === undefined || same(value, base) ? undefined : value
  const color = (a: string, b: string) => sameColor(a, b)
  const plots = pruneRecord(Object.fromEntries(rows.plots.map((row) => {
    const own = plotStyleOf(style, row.id)
    const palette = row.palette === null || own.palette === undefined ? undefined : pruneRecord(
      Object.fromEntries(Object.entries(own.palette).map(([index, c]) => [index, differs(c, row.palette![Number(index)] ?? "", color)]))
    )
    return [row.id, prune({
      visible: own.visible === false ? false : undefined,
      color: differs(own.color, row.color, color),
      lineWidth: differs(own.lineWidth, row.lineWidth),
      lineStyle: differs(own.lineStyle, row.lineStyle),
      type: differs(own.type, row.type),
      palette
    })]
  })))
  const levels = pruneRecord(Object.fromEntries(rows.levels.map((row) => {
    const own = levelStyleOf(style, row.id)
    return [row.id, prune({
      visible: own.visible === false ? false : undefined,
      color: differs(own.color, row.color, color),
      lineWidth: differs(own.lineWidth, row.lineWidth),
      lineStyle: differs(own.lineStyle, row.lineStyle),
      price: differs(own.price, row.price)
    })]
  })))
  const fills = pruneRecord(Object.fromEntries(rows.fills.map((row) => {
    const own = fillStyleOf(style, row.id)
    return [row.id, prune({
      visible: own.visible === false ? false : undefined,
      color: row.color === null ? undefined : differs(own.color, row.color, color)
    })]
  })))
  return prune({
    plots,
    levels,
    fills,
    precision: style.precision,
    labelsOnPriceScale: style.labelsOnPriceScale === false ? false : undefined,
    valuesInStatusLine: style.valuesInStatusLine === false ? false : undefined,
    inputsInStatusLine: style.inputsInStatusLine === false ? false : undefined
  }) as IndicatorStyle | undefined
}

/** Colours a new copy of an overlay indicator takes in turn, so copies on the price pane can be told apart. */
const CYCLE = ["#2962FF", "#FF9800", "#E91E63", "#4CAF50", "#9C27B0", "#00BCD4", "#F23645", "#089981", "#673AB7", "#FFEB3B"]

/**
 * The style of a newly added indicator: none for the first copy (the
 * indicator's own colours, as TradingView); for another copy of an overlay
 * indicator (a second EMA), the first colour of the cycle no copy uses yet.
 */
export const nextInstanceStyle = (
  entry: IndicatorRegistryEntry,
  existing: ReadonlyArray<IndicatorInstance>
): IndicatorStyle | undefined => {
  if (!entry.overlay) return undefined
  const siblings = existing.filter((instance) => instance.id === entry.id)
  if (siblings.length === 0) return undefined
  const primary = styleRows(entry).plots[0]
  if (primary === undefined) return undefined
  // A plot drawn in its palette's colours (MA Ribbon) is recoloured through the palette's first entry.
  const base = primary.palette?.[0] ?? primary.color
  const colorOf = (instance: IndicatorInstance) => {
    const own = plotStyleOf(instance.style, primary.id)
    return (primary.palette === null ? own.color : own.palette?.["0"]) ?? base
  }
  const used = siblings.map(colorOf)
  const color = CYCLE.find((candidate) => !used.some((u) => sameColor(opaque(u), candidate))) ?? CYCLE[siblings.length % CYCLE.length]!
  if (sameColor(color, base)) return undefined
  return { plots: { [primary.id]: primary.palette === null ? { color } : { palette: { "0": color } } } }
}
